<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch } from "vue";

import actions from "~/actions";
import { getPropertyDisplayTitle, getPropertyPartialTask, getPropertyValueFromTask } from "~/common/properties";
import AiSpinner from "~/components/dumb/AiSpinner.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import FillOutWithAiItem from "~/components/dumb/FillOutWithAiItem.vue";
import NumberEditor from "~/components/dumb/NumberEditor.vue";
import SizeChip from "~/components/dumb/SizeChip.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { notify } from "~/components/notifications";
import {
  EXPONENTIAL_SIZES,
  FIBONACCI_SIZES,
  getSizeOptions,
  LINEAR_SIZES,
  SIZE_FORMAT_TO_COLOR_MAP,
  TSHIRT_SIZE_TO_NUMBER_SIZE,
  TSHIRT_SIZE_TO_PRETTY_LABEL,
  TSHIRT_SIZES_INCLUDING_NULL,
  UNSIZED_SIZE_LABEL,
} from "~/constants/size";
import { colorsByTheme } from "~/constants/style";
import { SizeFieldIcon, SizeIcon } from "~/icons";
import { CommandId, DropdownMenuItemKind, EditorMode, NotificationType, Placement, SizeFormat } from "~/shared/enums";
import type { PropertyDefaultSize, PropertyValueForKind, Task } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore } from "~/stores";
import { generateFieldRecommendation } from "~/utils/recommendation";

type Value = PropertyValueForKind<PropertyDefaultSize>;

const SIZE_FORMATS = new Map([
  [SizeFormat.LINEAR, getSizeOptions(LINEAR_SIZES)],
  [SizeFormat.FIBONACCI, getSizeOptions(FIBONACCI_SIZES)],
  [SizeFormat.EXPONENTIAL, getSizeOptions(EXPONENTIAL_SIZES)],
  [
    SizeFormat.TSHIRT,
    TSHIRT_SIZES_INCLUDING_NULL.map((size) => ({
      size: TSHIRT_SIZE_TO_NUMBER_SIZE.get(size) ?? null,
      label: TSHIRT_SIZE_TO_PRETTY_LABEL.get(size) ?? UNSIZED_SIZE_LABEL,
    })),
  ],
]);

const props = defineProps<{
  property: PropertyDefaultSize;
  tasks: Task[];
  defaultValue?: Value;
  editorMode: EditorMode;
  value?: Value;
  showText?: boolean;
  onAfterClose?: (value: Value) => void;
}>();

const emit = defineEmits<{
  update: [size?: Value];
  reject: [];
}>();

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();

const numberEditor = ref<InstanceType<typeof NumberEditor> | null>(null);

const colors = computed(() => colorsByTheme[pageStore.theme]);

const feedbackTooltipNode = ref<HTMLDivElement | null>(null);
const dropdownMenu = ref<InstanceType<typeof DropdownMenu> | null>(null);

const selectedSize = computed(() => {
  if (props.editorMode === EditorMode.FORM || props.editorMode === EditorMode.AUTOMATION) {
    return props.value ?? null;
  }

  const firstTaskSize = getPropertyValueFromTask(props.property, props.tasks[0]);
  return props.tasks.every((task) => getPropertyValueFromTask(props.property, task) === firstTaskSize)
    ? firstTaskSize
    : null;
});

const loading = computed(() => appStore.isLoadingTasksProperty(props.tasks, props.property.duid));

const isChipRecommendationMode = computed(() => props.editorMode === EditorMode.CHIP_RECOMMENDATION);
const isChipMode = computed(() => props.editorMode === EditorMode.CHIP || isChipRecommendationMode.value);
const isListMode = computed(() => props.editorMode === EditorMode.LIST);
const isDetailBaseMode = computed(() => props.editorMode === EditorMode.DETAIL);
const isDetailRecommendationMode = computed(() => props.editorMode === EditorMode.DETAIL_RECOMMENDATION);
const isTaskDetailMode = computed(() => isDetailBaseMode.value || isDetailRecommendationMode.value);
const isContextMenuMode = computed(() => props.editorMode === EditorMode.CONTEXT_MENU);
const isFormMode = computed(() => props.editorMode === EditorMode.FORM);
const isAutomationMode = computed(() => props.editorMode === EditorMode.AUTOMATION);
const isFormOrAutomationMode = computed(() => isFormMode.value || isAutomationMode.value);

const isFreeEntry = computed(() => props.property.adtl.format === SizeFormat.FREE_ENTRY);

const selectedTasks = computed(() =>
  !isListMode.value
    ? props.tasks
    : dataStore.getTasksByDuidsOrderedIfLoaded([...appStore.selectedTaskDuids], {
        includeTrashed: false,
        includeDraft: false,
      })
);
const selectedTaskDuids = computed(() => selectedTasks.value.map((e) => e.duid));

const updateSize = (size: Value) => {
  dataStore.updateTasks(
    selectedTasks.value.map((task) => ({ duid: task.duid, ...getPropertyPartialTask(props.property, task, size) }))
  );
};

const generateRecommendation = async () => {
  const tempSelectedTasks = selectedTaskDuids.value;
  if (tempSelectedTasks.length > 100) {
    notify({ message: "Dart AI cannot fill out more than 100 tasks at once", type: NotificationType.ERROR });
    return;
  }

  appStore.startLoadingAiTaskProperties(tempSelectedTasks, props.property.duid);
  const [taskUpdates, recDuids] = await generateFieldRecommendation(
    selectedTasks.value,
    "size",
    props.defaultValue ?? null
  );
  dataStore.updateTasks(taskUpdates);
  appStore.finishLoadingAiTaskProperties(tempSelectedTasks, props.property.duid);

  actions.visualization.showFeedbackTooltipForTask(
    tempSelectedTasks,
    recDuids,
    props.editorMode,
    feedbackTooltipNode.value ?? null
  );
};

const rejectRecommendation = () => {
  updateSize(props.defaultValue ?? null);
  emit("update");
  emit("reject");
};

const sizeColorMap = computed(() => SIZE_FORMAT_TO_COLOR_MAP.get(props.property.adtl.format));

const dropdownSections = computed(() => {
  const firstTaskSize = isFormOrAutomationMode.value
    ? props.value
    : getPropertyValueFromTask(props.property, props.tasks[0]);
  const sizeOfSelectedTasks = isFormOrAutomationMode.value
    ? props.value
    : selectedTasks.value.every((task) => getPropertyValueFromTask(props.property, task) === firstTaskSize)
      ? firstTaskSize
      : undefined;

  return [
    {
      title: getPropertyDisplayTitle(props.property),
      items: (SIZE_FORMATS.get(props.property.adtl.format) ?? []).map(({ size: numberSize, label: title }) => {
        const hasSize = numberSize !== null;
        const isSelected = numberSize === sizeOfSelectedTasks;
        return {
          title,
          kind: DropdownMenuItemKind.BUTTON,
          icon: SizeIcon,
          iconArgs: {
            class: "icon-sm",
            format: props.property.adtl.format,
            ...(hasSize ? { size: numberSize, style: { color: sizeColorMap.value?.get(numberSize) } } : {}),
          },
          disabled: isSelected && !pageStore.isMobile,
          selected: isSelected && pageStore.isMobile,
          onClick: () => {
            if (isFormOrAutomationMode.value) {
              emit("update", numberSize);
              return;
            }

            updateSize(numberSize);
            emit("update");
          },
        };
      }),
    },
  ];
});

const handleAfterClose = () => {
  props.onAfterClose?.(selectedSize.value);
};

const showEditor = ref(false);

const descendantTasks = computed(() =>
  props.tasks[0]
    ? dataStore.getTasksByDuidsOrderedIfLoaded(dataStore.getDescendantDuidsUiOnly(props.tasks[0]), {
        includeTrashed: false,
        includeDraft: false,
      })
    : []
);

const hasSubtasks = computed(() => descendantTasks.value.length > 0 && props.property.adtl.rollup);

const onClick = () => {
  actions.visualization.selectRowByIdAndScroll(props.tasks[0].duid);
  if (isFreeEntry.value) {
    showEditor.value = true;
    nextTick(() => numberEditor.value?.focus());
  }
};

watch(selectedTasks, (newSelectedTasks) => {
  if (newSelectedTasks.length === 0 && showEditor.value) {
    showEditor.value = false;
  }
});

onUnmounted(() => {
  appStore.showFeedbackTooltip(null, []);
});

const open = () => {
  if (isFreeEntry.value) {
    showEditor.value = true;
    nextTick(() => numberEditor.value?.focus());
    return;
  }
  dropdownMenu.value?.open();
};

defineExpose({
  open,
  isOpen: computed(() => dropdownMenu.value?.isOpen),
});
</script>

<template>
  <DropdownMenu
    v-if="!isFreeEntry"
    ref="dropdownMenu"
    :container="isChipMode ? '#dart-task-creation-modal-wrapper' : isFormMode ? '#dart-form-wrapper' : undefined"
    :sections="dropdownSections"
    :block="isListMode || isFormOrAutomationMode"
    :height-block="isListMode || isFormOrAutomationMode"
    :distance="isFormOrAutomationMode ? 2 : undefined"
    :skidding="isFormOrAutomationMode ? -1 : undefined"
    :cover="!isContextMenuMode && !isFormOrAutomationMode"
    :placement="isContextMenuMode ? Placement.RIGHT_TOP : Placement.BOTTOM_LEFT"
    :style="{ '--background': colors.borderVlt, '--highlight': colors.borderMd }"
    :on-after-close="handleAfterClose"
    :header-title="`Set ${getPropertyDisplayTitle(property).toLowerCase()}`"
    mobile-include-done>
    <template v-if="!isFormOrAutomationMode" #footer="{ close }">
      <FillOutWithAiItem :close="close" @recommend="generateRecommendation" />
    </template>
    <DropdownMenuItemContent
      v-if="isContextMenuMode"
      :icon="SizeFieldIcon"
      :title="`Change ${getPropertyDisplayTitle(property).toLowerCase()}`"
      is-submenu />
    <Tooltip
      v-else
      :disabled="isChipRecommendationMode || isFormOrAutomationMode"
      :command-id="CommandId.CHANGE_SIZE"
      :block="isListMode || isFormOrAutomationMode"
      :height-block="isListMode || isFormOrAutomationMode"
      :class="isTaskDetailMode && 'w-full'">
      <div
        ref="feedbackTooltipNode"
        class="group/size flex size-full max-w-full items-center text-sm"
        :class="{
          'px-1.5': isListMode,
          'justify-start rounded': isTaskDetailMode,
          'hover:bg-lt': isDetailBaseMode,
          'bg-recommendation-base/10 hover:bg-recommendation-base/20 dark:hover:bg-recommendation-base/20':
            isDetailRecommendationMode,
          'rounded border border-oncolor px-1 py-0.5': loading && isChipMode,
        }">
        <div v-if="loading" class="flex size-full items-center justify-center px-4 py-0.5">
          <AiSpinner class="icon-sm" />
        </div>
        <SizeChip
          v-else
          :task="isChipRecommendationMode || isFormOrAutomationMode ? undefined : props.tasks[0]"
          :value="selectedSize"
          :property="props.property"
          :show-border="isChipMode"
          :show-if-empty="isTaskDetailMode || isChipMode || isFormOrAutomationMode"
          :editor-mode="editorMode"
          @accept="emit('update')"
          @reject="rejectRecommendation" />
        <div
          v-if="isListMode && selectedSize === null"
          class="flex size-full items-center justify-center text-transparent group-hover/size:text-vlt">
          <SizeFieldIcon class="icon-xs" />
        </div>
      </div>
    </Tooltip>
  </DropdownMenu>
  <NumberEditor
    v-else-if="showEditor || (!hasSubtasks && isFreeEntry)"
    ref="numberEditor"
    :property="props.property"
    :tasks="props.tasks"
    :editor-mode="props.editorMode"
    :default-value="props.defaultValue"
    :value="props.value"
    @update="(value) => emit('update', value)" />

  <Tooltip
    v-else
    :disabled="isChipRecommendationMode || isFormOrAutomationMode"
    :text="`Change ${getPropertyDisplayTitle(property).toLowerCase()}`"
    :block="isListMode || isFormOrAutomationMode"
    :height-block="isListMode || isFormOrAutomationMode"
    :class="isTaskDetailMode && 'w-full'">
    <button
      type="button"
      class="flex size-full max-w-full cursor-pointer items-center text-sm"
      :class="{
        'px-1.5': isListMode,
        '!px-0': isListMode && isFreeEntry && hasSubtasks,
        'justify-start rounded': isTaskDetailMode,
        'hover:bg-lt': isDetailBaseMode,
        'bg-recommendation-base/10 hover:bg-recommendation-base/20 dark:hover:bg-recommendation-base/20':
          isDetailRecommendationMode,
      }"
      @click="onClick">
      <SizeChip
        :task="isChipRecommendationMode || isFormOrAutomationMode ? undefined : props.tasks[0]"
        :value="selectedSize"
        :property="props.property"
        :show-border="isChipMode"
        :show-if-empty="isTaskDetailMode || isChipMode || isFormOrAutomationMode"
        :editor-mode="editorMode"
        @accept="emit('update')"
        @reject="emit('reject')" />
    </button>
  </Tooltip>
</template>
